import React, { createContext, useContext, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useMilestones } from "@/hooks/useMilestones";
import MilestoneMoment from "@/components/MilestoneMoment";

/**
 * MilestonesContext — thin wrapper around `useMilestones` so any page can
 * fire `checkNow()` after a plant save / share create without duplicating
 * queue state per page. The bottom-sheet moment is mounted once (here) so
 * it survives route changes and stacks nicely across a bulk-save burst.
 *
 * Mount inside `<AuthProvider>` so `user?.user_id` is available.
 */
const MilestonesContext = createContext({
  checkNow: async () => [],
  current: null,
  queueLength: 0,
});

export function MilestonesProvider({ children }) {
  const { user } = useAuth();
  const { current, dismiss, checkNow, queueLength } = useMilestones(user?.user_id);

  const value = useMemo(() => ({ checkNow, current, queueLength }), [checkNow, current, queueLength]);

  return (
    <MilestonesContext.Provider value={value}>
      {children}
      {/* `key={current?.id}` remounts the component whenever the head of the
          queue changes so the mount-only entrance animation (`entered` state
          + `translate-y-8 → 0`) fires for every milestone, not just the
          first. Without this, subsequent cards render at opacity:0 forever. */}
      <MilestoneMoment key={current?.id || "empty"} milestone={current} onDismiss={dismiss} />
    </MilestonesContext.Provider>
  );
}

export function useMilestonesContext() {
  return useContext(MilestonesContext);
}
